import {
  DESKTOP_SYNC_MESSAGE_TYPES,
  isDesktopSyncBridgeMessage,
} from "@bezoned/desktop-bridge/desktop-sync-messaging.js";
import type {
  BinaryMessagePayload,
  InstanceInfo,
  JsonMessagePayload,
  RoomBroadcastPayload,
} from "@bezoned/desktop-bridge/events.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DesktopBridgeClient } from "./desktop-bridge-client.js";
import { DesktopBridgeContext } from "./desktop-bridge.context.js";

type ConnectionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "syncing"
  | "ready"
  | "waiting"
  | "reconnecting"
  | "disconnected";

interface DesktopBridgeProviderProps {
  children: React.ReactNode;
}

const getDesktopAppVersion = (payload: JsonMessagePayload): string | null => {
  if (!isDesktopSyncBridgeMessage(payload.data)) {
    return null;
  }

  if (payload.data.type === DESKTOP_SYNC_MESSAGE_TYPES.ON_CONNECT) {
    return payload.data.payload.sections.version?.desktopAppVersion ?? null;
  }

  if (payload.data.type === DESKTOP_SYNC_MESSAGE_TYPES.VERSION_UPDATE) {
    return payload.data.payload.sections.version.desktopAppVersion;
  }

  return null;
};

export const DesktopBridgeProvider = ({
  children,
}: DesktopBridgeProviderProps) => {
  const clientRef = useRef<DesktopBridgeClient | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>("idle");
  const [instanceId, setInstanceId] = useState<string | null>(null);
  const [instances, setInstances] = useState<InstanceInfo[]>([]);
  const [desktopAppVersion, setDesktopAppVersion] = useState<string | null>(
    null
  );

  // Subscriber refs for message handlers
  const jsonHandlersRef = useRef<Set<(payload: JsonMessagePayload) => void>>(
    new Set()
  );
  const binaryHandlersRef = useRef<
    Set<(payload: BinaryMessagePayload) => void>
  >(new Set());
  const broadcastHandlersRef = useRef<
    Set<(payload: RoomBroadcastPayload) => void>
  >(new Set());

  useEffect(() => {
    const client = new DesktopBridgeClient();
    clientRef.current = client;

    client.setListeners({
      onBinaryMessage: (payload) => {
        for (const handler of binaryHandlersRef.current) {
          handler(payload);
        }
      },
      onInstanceIdChange: setInstanceId,
      onInstanceListChange: setInstances,
      onJsonMessage: (payload) => {
        const nextDesktopAppVersion = getDesktopAppVersion(payload);
        if (nextDesktopAppVersion) {
          setDesktopAppVersion((currentVersion) =>
            currentVersion === nextDesktopAppVersion
              ? currentVersion
              : nextDesktopAppVersion
          );
        }

        for (const handler of jsonHandlersRef.current) {
          handler(payload);
        }
      },
      onRoomBroadcast: (payload) => {
        for (const handler of broadcastHandlersRef.current) {
          handler(payload);
        }
      },
      onStatusChange: setStatus,
    });

    return () => {
      client.disconnect();
      clientRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (
      status === "connecting" ||
      status === "connected" ||
      status === "syncing" ||
      status === "ready" ||
      status === "reconnecting"
    ) {
      return;
    }

    setDesktopAppVersion(null);
  }, [status]);

  const connect = useCallback(() => {
    clientRef.current?.connect("teams", "Teams Tab");
  }, []);

  const disconnect = useCallback(() => {
    clientRef.current?.disconnect();
  }, []);

  const sendJson = useCallback((room: string, data: unknown) => {
    clientRef.current?.sendJson(room, data);
  }, []);

  const sendBinary = useCallback(
    (room: string, data: ArrayBuffer, mimeType: string, filename?: string) => {
      clientRef.current?.sendBinary(room, data, mimeType, filename);
    },
    []
  );

  const joinRoom = useCallback((room: string) => {
    clientRef.current?.joinRoom(room);
  }, []);

  const leaveRoom = useCallback((room: string) => {
    clientRef.current?.leaveRoom(room);
  }, []);

  const broadcast = useCallback((room: string, data: unknown) => {
    clientRef.current?.broadcast(room, data);
  }, []);

  const onJsonMessage = useCallback(
    (handler: (payload: JsonMessagePayload) => void) => {
      jsonHandlersRef.current.add(handler);
      return () => {
        jsonHandlersRef.current.delete(handler);
      };
    },
    []
  );

  const onBinaryMessage = useCallback(
    (handler: (payload: BinaryMessagePayload) => void) => {
      binaryHandlersRef.current.add(handler);
      return () => {
        binaryHandlersRef.current.delete(handler);
      };
    },
    []
  );

  const onRoomBroadcast = useCallback(
    (handler: (payload: RoomBroadcastPayload) => void) => {
      broadcastHandlersRef.current.add(handler);
      return () => {
        broadcastHandlersRef.current.delete(handler);
      };
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      __ctx_init: true,
      broadcast,
      connect,
      desktopAppVersion,
      disconnect,
      instanceId,
      instances,
      joinRoom,
      leaveRoom,
      onBinaryMessage,
      onJsonMessage,
      onRoomBroadcast,
      sendBinary,
      sendJson,
      status,
    }),
    [
      status,
      instanceId,
      instances,
      desktopAppVersion,
      connect,
      disconnect,
      sendJson,
      sendBinary,
      joinRoom,
      leaveRoom,
      broadcast,
      onJsonMessage,
      onBinaryMessage,
      onRoomBroadcast,
    ]
  );

  return (
    <DesktopBridgeContext.Provider value={contextValue}>
      {children}
    </DesktopBridgeContext.Provider>
  );
};
